import * as React from 'react';

import type * as api from '@gitbook/api-client';
import { PlainCodeBlock } from '@gitbook/react-uikit';
import { DialogCloseButton, Heading, PageSection, StepControls } from '@gitbook/spine-react';

import { IntegrationStepWrapper } from '../../../Integrations';
import type { SetupFeatureFlagsStepProps } from '../../types';
import { FEATURE_FLAGS_PROVIDERS_SAMPLES } from '../../utils';

/**
 * Step showing that the install is completed.
 */
export function InstallCompletedStep(
    props: Omit<SetupFeatureFlagsStepProps, 'onNext'> & {
        integration: api.Integration;
    }
) {
    const headingId = React.useId();
    const providerName = props.integration.title.toLowerCase().replace(/\s+/g, '');
    const providerTitle = props.integration.title;
    assertValidProviderName(providerName);
    const sdkCodeSample = FEATURE_FLAGS_PROVIDERS_SAMPLES[providerName];

    return (
        <IntegrationStepWrapper
            title="The installation is completed"
            headingId={headingId}
            description={`You've finished setting up your site to sync with ${providerTitle}`}
        >
            <PageSection>
                <Heading level={3}>You're all set!</Heading>
                <p>
                    Your GitBook site is now connected to <strong>{providerTitle}</strong>, and will
                    sync your feature flags automatically.
                </p>
                <p>
                    You can now start writing adaptive content conditions using your feature flags,
                    available under:
                    <br />
                    <code>
                        visitor.claims.unsigned.{providerName}
                        .*
                    </code>
                </p>
                <p>
                    Be sure to check your site schema to see the available feature flags and ensure
                    they're exposed as expected.
                </p>
            </PageSection>

            <PageSection>
                <Heading level={3}>Add this snippet to your application</Heading>
                <p>
                    Before using your feature flags in conditions, make sure to add the following
                    code to your application. This will ensure GitBook can read the flags from{' '}
                    {providerTitle}:
                </p>
                <PlainCodeBlock syntax="javascript" code={sdkCodeSample} showToolbar={true} />
            </PageSection>

            <StepControls>
                <div className="flex w-full justify-end">
                    <DialogCloseButton label="Close" />
                </div>
            </StepControls>
        </IntegrationStepWrapper>
    );
}

type ProviderKey = keyof typeof FEATURE_FLAGS_PROVIDERS_SAMPLES;

function assertValidProviderName(key: string): asserts key is ProviderKey {
    if (!(key in FEATURE_FLAGS_PROVIDERS_SAMPLES)) {
        throw new Error(`Unrecognized feature flag provider: "${key}"`);
    }
}
